/**
 * predicates — composable, side-effect-free predicate helpers used by
 * `DesignerTool.applies_to`.
 *
 * Phase 13 P0 (June 2026)
 *
 * Why a dedicated module
 * ----------------------
 *
 * Every tool's `applies_to(ctx)` function is run during render (when
 * the shell asks the registry for the tool list for the current
 * scene). Two contracts must hold:
 *
 *   1. Pure & synchronous. No API calls, no setState, no Date.now()
 *      gating. The same ctx → the same answer.
 *   2. Defensive. The ctx may be partially populated during initial
 *      mount (catalog still loading, scene null, role claims pending).
 *      Predicates must return falsy on missing context rather than
 *      throwing — a thrown predicate would crash the entire shell.
 *
 * These helpers encapsulate both contracts so per-tool predicates
 * stay one-liners. Compose with `all` / `any` for richer rules.
 *
 * Examples
 * --------
 *
 *     applies_to: requiresCapability('telestration')
 *     applies_to: all(requiresSceneType('interactive_quiz'), requiresFormat('animated_board'))
 *     applies_to: any(requiresDomain('football'), requiresDomain('basketball'))
 *
 * Conventions
 * -----------
 *
 *   * `ctx` is the live `DesignerContextValue` returned by
 *     `useDesigner()`. The predicate is invoked with that exact
 *     object — see DesignerContext for the canonical shape.
 *   * Capabilities are read from `ctx.capabilities` (a Set or array).
 *     The shell normalizes both shapes; predicates accept either.
 *   * Format / subject come from `ctx.format` / `ctx.subject`
 *     (Phase-12 facets, materialized once per scene by the shell).
 *   * Domain comes from `ctx.domainPack?.id`.
 */

// ────────────────────────────────────────────────────────────────────
// Internal helpers
// ────────────────────────────────────────────────────────────────────

/**
 * Normalize capability access — `ctx.capabilities` may arrive as a
 * Set, an Array, or `undefined` (during initial mount before the
 * scene resolves). Returns a Set every time so callers can `.has()`.
 *
 * @param {*} caps
 * @returns {Set<string>}
 */
function asCapSet(caps) {
  if (caps instanceof Set) return caps;
  if (Array.isArray(caps)) return new Set(caps);
  return new Set();
}

/**
 * Wrap a predicate so it never throws — a buggy predicate logs and
 * falls back to `false` rather than killing the whole shell.
 *
 * @param {Function} fn
 * @returns {(ctx: any) => boolean}
 */
function safe(fn) {
  return (ctx) => {
    try {
      return Boolean(fn(ctx));
    } catch (err) {
      // Surfaced once per session via the shell's error toast.
      // Tests can assert on this by spying on console.warn.
      // eslint-disable-next-line no-console
      console.warn('[designer.predicate] threw — treating as false:', err);
      return false;
    }
  };
}

// ────────────────────────────────────────────────────────────────────
// Atomic predicates
// ────────────────────────────────────────────────────────────────────

/**
 * `requiresCapability(cap)` — the canonical binding rule.
 *
 * Tools should bind on capability whenever possible (rather than on
 * scene_type or domain) so they automatically work for any new
 * scene_type the catalog adds that declares the same capability.
 * That's the architectural lever that lets P2's whiteboard
 * graduation work without touching tool code.
 *
 * @param {string} cap
 * @returns {(ctx: any) => boolean}
 */
export function requiresCapability(cap) {
  if (!cap || typeof cap !== 'string') {
    throw new Error('requiresCapability: cap must be a non-empty string');
  }
  return safe((ctx) => asCapSet(ctx?.capabilities).has(cap));
}

/**
 * `requiresSceneType(scene_type)` — escape hatch for tools that
 * legitimately bind on scene_type identity (e.g. video-only zone
 * overlays).
 *
 * Prefer `requiresCapability` when the binding is conceptually
 * "this tool works on any scene that supports X". Use scene_type
 * only when the tool is fundamentally tied to one renderer.
 *
 * @param {string} sceneType
 */
export function requiresSceneType(sceneType) {
  if (!sceneType || typeof sceneType !== 'string') {
    throw new Error('requiresSceneType: sceneType must be a non-empty string');
  }
  return safe((ctx) => ctx?.scene?.scene_type === sceneType);
}

/**
 * `requiresFormat(quizFormat)` — Phase-12 interactive_quiz facet.
 *
 * Format gates the per-format quiz tools (e.g. MovementDurationSlider
 * is only relevant for `animated_board`).
 *
 * @param {string} quizFormat
 */
export function requiresFormat(quizFormat) {
  if (!quizFormat || typeof quizFormat !== 'string') {
    throw new Error('requiresFormat: quizFormat must be a non-empty string');
  }
  return safe((ctx) => ctx?.format === quizFormat);
}

/**
 * `requiresSubject(quizSubject)` — Phase-12 interactive_quiz facet.
 *
 * Subject gates the per-subject distractor pickers and tone-shift
 * helpers (e.g. coverage-recognition siblings vs concept siblings).
 *
 * @param {string} quizSubject
 */
export function requiresSubject(quizSubject) {
  if (!quizSubject || typeof quizSubject !== 'string') {
    throw new Error('requiresSubject: quizSubject must be a non-empty string');
  }
  return safe((ctx) => ctx?.subject === quizSubject);
}

/**
 * `requiresDomain(domainId)` — for tools registered by a domain pack.
 *
 * Use sparingly. Most domain tools should bind on a CAPABILITY the
 * domain pack declares on its scene_type (e.g.
 * `football.coverage_catalog`) so the same tool works for any pack
 * that opts in by adding the capability to its catalog row.
 *
 * @param {string} domainId
 */
export function requiresDomain(domainId) {
  if (!domainId || typeof domainId !== 'string') {
    throw new Error('requiresDomain: domainId must be a non-empty string');
  }
  return safe((ctx) => (ctx?.domainPack?.id || ctx?.scene?.domain_id) === domainId);
}

/**
 * `requiresAppSurface(surface)` — gates tools to a specific learner-
 * app/coach-app/admin-app surface.
 *
 * Useful when a tool only makes sense in one shell (e.g. the Audit
 * tab's findings-renderer is `app.platform_admin` only).
 *
 * @param {string} surface
 */
export function requiresAppSurface(surface) {
  if (!surface || typeof surface !== 'string') {
    throw new Error('requiresAppSurface: surface must be a non-empty string');
  }
  return safe((ctx) => ctx?.appSurface === surface);
}

// ────────────────────────────────────────────────────────────────────
// Combinators
// ────────────────────────────────────────────────────────────────────

/**
 * `all(...predicates)` — logical AND. Returns `true` iff every
 * predicate returns truthy for the given ctx.
 *
 * Empty list returns `true` (vacuously). This matches the catalog's
 * "no constraint = unconstrained" semantics elsewhere in the
 * platform.
 *
 * @param  {...((ctx: any) => boolean)} preds
 */
export function all(...preds) {
  preds.forEach((p, i) => {
    if (typeof p !== 'function') {
      throw new Error(`all: argument #${i} is not a predicate function`);
    }
  });
  if (preds.length === 0) return safe(() => true);
  return safe((ctx) => preds.every((p) => p(ctx)));
}

/**
 * `any(...predicates)` — logical OR. Returns `true` iff any
 * predicate returns truthy.
 *
 * Empty list returns `false` (vacuously) so an unconfigured tool
 * doesn't accidentally bind universally.
 *
 * @param  {...((ctx: any) => boolean)} preds
 */
export function any(...preds) {
  preds.forEach((p, i) => {
    if (typeof p !== 'function') {
      throw new Error(`any: argument #${i} is not a predicate function`);
    }
  });
  if (preds.length === 0) return safe(() => false);
  return safe((ctx) => preds.some((p) => p(ctx)));
}

/**
 * `not(predicate)` — logical NOT. Useful for "tool that hides when
 * the catalog declares X" cases.
 *
 * @param {(ctx: any) => boolean} pred
 */
export function not(pred) {
  if (typeof pred !== 'function') {
    throw new Error('not: argument must be a predicate function');
  }
  return safe((ctx) => !pred(ctx));
}

/**
 * `always` / `never` — terminal predicates. `always` is the default
 * for system-chrome tools (metadata form, save button) that bind
 * to every scene unconditionally.
 */
export const always = safe(() => true);
export const never  = safe(() => false);
